/* eslint-disable no-magic-numbers */
import { Http } from '@/services/http'
import { Injectable } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import {
  GroupPolicyDrawerService
} from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'
import { hasExistingStrategyId } from '@/utils/strategy-id'
import { i18n } from '@/i18n/index'
export const SETTING_PANE_NAMES = {
  regularPhysicalExamination: 'regular-physical-examination',
  timingAntivirus: 'timing-antivirus',
  flowControl: 'flow-control',
  onlineBlackAndWhiteList: 'online-black-and-white-list',
  earlyWarningAndMonitoring: 'early-warning-and-monitoring',
  leakFix: 'leak-fix',
  usbManage: 'usb-manage',
  baselineScan: 'baseline-scan',
  realTimeProtection: 'real-time-protection',
  /** 基本设置 */
  basicSettings: 'basic-settings',
  /** 安全保护 */
  securityProtection: 'security-protection',
}

export enum GroupPolicyTypes {
  RegularPhysicalExamination = '12', // 定时体检
  TimingAntivirus = '13', // 定时查杀
  FlowControl = '3', // 流量管控
  OnlineBlackAndWhiteList = '4', // 网络黑白名单
  EarlyWarningAndMonitoring = '5', // 预警监控
  LeakFix = '14', // 漏洞修复
  USBManage = '7', // 设备管控
  LinuxUSBManage = '18', // 设备管控
  BaselineScan = '15', // 基线扫描
  LinuxBaselineScan = '20', // Linux基线扫描
  LinuxTimingAntivirus = '16', // 定时查杀
  LinuxLeakFix = '17', // 漏洞修复
  LinuxOnlineBlackAndWhiteList = '11', // 网络黑白名单
  MacTimingAntivirus = '19', // 定时查杀
  RealTimeProtection = '37', // 实时防护
  BasicSettings = '38', // 基本设置
  SecurityProtection = '22', // 安全保护
}

export enum ExaminationTaskTypes {
  OnlyRemind = 0, // 仅提醒，不强制体检和修复
  RemindLaterExaminationAndRepair = 1, // 提醒并强制三分钟后体检和修复
  DonotRemindAndExamination = 2, // 不提醒，强制体检但不修复
  DonotRemindExaminationAndRepair = 3 // 不提醒，强制体检并修复
}

export enum Frequency {
  EveryDay = 86400, // 每天
  EveryThreeDays = 259200, // 每三天
  Weekly = 604800, // 每周
  Monthly = 2592000 // 每月
}

export enum EarlyWarningFrequency {
  ThirtyMin = 1800, // 30分钟
  // FortyFiveMin = 2700, // 45分钟
  SixtyMin = 3600, // 60分钟
  OneHundredAndTwentyMin = 7200, // 120分钟
  OneHundredAndEightyMin = 10800 // 180分钟
}

export enum IntervalTypes {
  Unlimited = 0, // 无限制
  MoreThanOneDay = 1, // 超过1天未体检设备
  MoreThanThreeDay = 2, // 超过3天未体检设备
  MoreThanSevenDay = 3 // 超过7天未体检设备
}

export enum ScanTypes {
  Lightning = 0, // 闪电查杀
  Overall = 1 // 全盘查杀
}

export enum ScanTaskTypes {
  OnlyRemind = 0, // 仅提醒，不强制扫描和查杀
  RemindLaterScanAndAntiVirus = 1, // 提醒并强制三分钟后扫描和查杀
  DonotRemindAndScan = 2, // 不提醒，强制扫描但不查杀
  DonotRemindScanAndAntiVirus = 3 // 不提醒，强制扫描并查杀
}

export enum FlowUnits {
  KB = 0,
  MB = 1
}

export enum OnlineLimitTypes {
  Unlimited = 0, // 无限制
  BlackList = 1, // 黑名单
  WhiteList = 2 // 白名单
}

export interface QueryParams {
  id: string
  type: GroupPolicyTypes
}

export interface SettingsRes {
  /** 策略id */
  id: string
  /** 策略名称 */
  name: string
  /** 策略内容(json) */
  strategy: any
}

export interface SaveParams {
  /** 策略id */
  id: string
  /** 策略名称 */
  name?: string
  /** 策略内容(json) */
  strategy: string
  /** 策略类型 */
  type: GroupPolicyTypes
  /** 0-分组模式，1-终端模式，未传时由 saveSettings 默认 0 */
  bind_mode?: number
  /** 分组模式下应用分组 */
  group_ids?: (string | number)[]
  /** 终端模式下应用终端 */
  device_ids?: string[]
}
export interface TaskTimeSetting {
  target?: number // 每周的星期几，每月的几号
  start_min: number // 离当天0点的分钟数
  end_min?: number // 离当天0点的分钟数
  frequency?: number, // 频率，1-每日，2-每周，3-每月，非必须
  extra1?: any, // 业务自定义字段，不同业务可描述不同字段
  extra2?: any, // 业务自定义字段，不同业务可描述不同字段
}

export interface RegularPhysicalExaminationSettings {
  main_switch?: boolean // 开关
  task_type?: number // 任务执行方式
  interval_type?: number // 体检对象
  frequency?: number, // 频率，1-每日，2-每周，3-每月
  regular_tasks?: TaskTimeSetting[]
}

export interface TimingAntivirusSettings {
  main_switch?: boolean // 开关
  scan_type?: number, // 扫描类型
  task_type?: number // 任务执行方式
  interval_type?: number // 限制类型
  frequency?: number, // 频率，1-每日，2-每周，3-每月
  regular_tasks?: TaskTimeSetting[],
}

export interface FlowControlSettings {
  switch?: boolean // 开关
  up_speed?: number // 上行速度
  down_speed?: number // 下行速度
  up_unit?: number // 上行单位
  down_unit?: number // 下行单位
}

export interface OnlineBlackAndWhiteListSettings {
  limit_type?: number // 限制类型
  black_list?: string // 黑名单
  white_list?: string // 白名单
}

export interface EarlyWarningAndMonitoringSettings {
  frequency?: number // 频率
  morning_start?: number // 上午开始时间,距离0点秒数
  morning_end?: number // 上午结束时间,距离0点秒数
  afternoon_start?: number // 下午开始时间,距离0点秒数
  afternoon_end?: number // 下午结束时间,距离0点秒数
  exam_switch?: boolean // 体检开关
  exam_score_limit?: number // 体检预警分数线
  virus_switch?: boolean // 查杀开关
  system_disk_used_switch?: boolean // 系统盘占用预警开关
  system_disk_used?: number // 系统盘占用预警百分比
  hard_disk_used_switch?: boolean // 总体磁盘占用预警开关
  hard_disk_used?: number // 总体磁盘占用预警百分比
  cpu_used_switch?: boolean // CPU占用预警开关
  cpu_used?: number // CPU占用预警百分比
  cpu_used_duration?: number // CPU占用预警持续时间
  memory_used_switch?: boolean // 内存占用预警开关
  memory_used?: number // 内存占用预警百分比
  memory_used_duration?: number // 内存占用预警持续时间
  cpu_temperature_switch?: boolean // CPU温度预警开关
  cpu_temperature?: number // CPU温度预警线
  cpu_temperature_duration?: number // CPU温度预警持续时间
  disk_temperature_switch?: boolean // 磁盘温度预警开关
  disk_temperature?: number // 磁盘温度预警线
  disk_temperature_duration?: number //  磁盘预警持续时间
  net_up_speed_switch?: boolean // 上行网速预警开关
  net_up_speed?: number // 上行网速预警最高速度
  net_up_speed_duration?: number // 上行网速预警持续时间
  net_down_speed_switch?: boolean // 下行网速预警开关
  net_down_speed?: number // 下行网速预警最高速度
  net_down_speed_duration?: number // 下行网速预警持续时间
}

export interface LeakFixSettings{
  scan_frequency: number
  scan_tasks?: TaskTimeSetting[] // 漏洞扫描每天任务
  restart_remind_type: number
  restart_remind_interval_min: number
  blue_screen_repair_switch: boolean
  repair_task_main_switch: boolean
  frequency: number
  regular_tasks?: TaskTimeSetting[]
  repair_os_list: string[]
  warn_level_list: number[]
}
export interface OsOption {
  /** 前端展示名 */
  display_name: string
  /** 对应值 */
  value: string
}

export interface WhiteList {
  /** 存储设备id */
  peripheral_id: string
  /** 备注 */
  remark: string
}

export interface USBManageSettings{
  /** 管控类型，1-无管控策略,2-禁止读写 */
  control_type: number
  /** 是否提醒 */
  is_remind: boolean
  /** 名单列表 */
  white_list: WhiteList[]
}

export interface BaselineScanCustomItem {
  item_id: string
  data_value: string
  fix_value?: string
}

export interface BaselineScanSettings {
  /** 总开关 */
  main_switch: boolean
  frequency: number
  scan_type?: number
  scan_item_ids?: string[]
  regular_tasks?: TaskTimeSetting[]
  scan_item_custom?: BaselineScanCustomItem[]
}

export interface VirusDetectionSettings {
  /** 是否扫描所有类型文件, 1.扫描所有文件 0.仅扫描程序和文档文件. 默认为0 */
  scan_all_file: number,
  /** 全盘查杀时进入压缩包扫描, 1为勾选，0为不勾选. 默认为0 */
  fullscan_extract: number,
  /** 全盘查杀时进入光驱扫描, 1为勾选，0为不勾选. 默认为0 */
  fullscan_cdrom: number,
  /** 指定位置查杀时进入压缩包扫描, 1为勾选，0为不勾选. 默认为1 */
  customscan_extract: number,
  /** 发现病毒时处理方式, 1. 自动处理 0. 手动处理. 默认为0 */
  auto_fix_file: number,
  /** 清除病毒前自动备份, 1为勾选，0为不勾选. 默认为1 */
  backup_file: number,
  /** 病毒查杀模式 1. 强力模式 0. 智能模式. 默认为0 */
  strong_scan: number,
  /** 宏病毒强力修复模式, 1为勾选，0为不勾选. 默认为0 */
  macro_deepScan: number,
  /** 轻盈防护开关 1-开 0-关 */
  light_weight_switch: number
}

export interface SystemProtectionSettings {
  /** 监控模式 1. 全盘监控v1 2.智能监控 3.全盘监控v2 4.全盘监控v3 5.普通监控. 默认为2 */
  spy_mode: number,
  /** 系统内核加固，1为勾选，0为不勾选. 默认为1 */
  kernel_stable: number,
  /** 提示office文档安全结果，1为勾选，0为不勾选. 默认为1 */
  office_remind: number,
  /** 开启诱饵式勒索防护, 1为勾选，0为不勾选. 默认为0 */
  extort_protect: number,
  /** 自动拦截骚扰弹窗, 1为勾选，0为不勾选. 默认为0 */
  auto_block_pop: number,
  /** 软件安装提醒, 1为勾选，0为不勾选. 默认为1 */
  soft_install_tips: number,
}

export interface RealTimeProtectionSettings {
  /** 主开关 1-开 0-关 */
  main_switch: number,
  /** 程序防护开关 1-开 0-关 */
  program_switch: number,
  /** 加载驱动开关 1-开 0-关 */
  load_driver_switch: number,
  /** 注册表防护开关 1-开 0-关 */
  reg_switch: number,
  /** 注入防护开关 1-开 0-关 */
  inject_switch: number,
  /** 关键区域防护开关 1-开 0-关 */
  key_area_switch: number,
  /** 桌面防护开关 1-开 0-关 */
  desktop_switch: number,
  /** U盘防护开关 1-开 0-关 */
  udisk_switch: number,
  /** 内存防护开关 1-开 0-关 */
  memory_defend_switch: number
  /** 轻盈防护开关 1-开 0-关 */
  light_weight_switch: number
}

/** 代理设置 */
export interface ProxySettings {
  /** 代理方法：0=不使用代理，1=IE代理，2=http代理或者sock5代理（手动配置） */
  method: number
  /** 代理模式：1=http代理，5=sock5代理（当method=2时使用） */
  mode: number
  /** 是否需要认证：0=不需要，1=需要 */
  require_auth: number
  /** 代理主机地址 */
  host?: string
  /** 代理端口 */
  port?: number
  /** 认证用户名 */
  user?: string
  /** 认证密码 */
  password?: string
}

/** 基本设置子字段 */
export interface BasicSubSettings {
  /** 金山云安全计划：1=启用，0=禁用 */
  cloud_security: number
  /** 自动进入免打扰模式：1=启用，0=禁用 */
  auto_interrupt: number
  /** 启用安全消息中心：1=启用，0=禁用 */
  security_msg_center: number
  /** 弹窗消息定位：1=启用，0=禁用 */
  pop_location: number
  /** 黑客恶意扫描提醒：1=启用，0=禁用 */
  allow_notify_hackattack: number
  /** 产品功能介绍弹窗（JP专用）：1=启用，0=禁用 */
  allow_notify_spreadinfo: number
  /** 拦截蠕虫攻击提醒：1=启用，0=禁用 */
  allow_notify_wormattack: number
  /** 高危漏洞提醒：1=启用，0=禁用 */
  allow_notify_leak: number
  /** 自动升级：1=启用，0=禁用 */
  auto_upgrade: number
  /** 升级完成通知：1=启用，0=禁用 */
  upgrade_notify: number
  /** 清理升级产生的临时文件：1=启用，0=禁用 */
  clean_upgrade_trash: number
  /** 右键菜单是否添加扫描：1=添加，0=不添加 */
  rightmenu_addscan: number
  /** 右键菜单是否添加文件粉碎：1=添加，0=不添加 */
  rightmenu_addcrush: number
  /** 右键菜单是否添加C盘优化（JP专用）：1=添加，0=不添加 */
  rightmenu_sysslim_menu: number
  /** 右键菜单是否添加到收藏夹（JP专用）：1=添加，0=不添加 */
  rightmenu_accfavorite: number
  /** 右键菜单是否添加大文件/重复文件整理（JP专用）：1=添加，0=不添加 */
  rightmenu_largecleanup_menu: number
  /** 恶意弹窗右上角是否显示"拦截弹窗": 1=显示，0=不显示 */
  soft_purifier_switch: number
  /** 鼠标移动到百宝箱时是否显示"常用工具"快捷入口: 1=显示，0=不显示 */
  tool_switch: number
  /** 代理设置 */
  proxy: ProxySettings
}

/** 基本设置（type=21 完整结构） */
export interface BasicSettingsStrategy {
  basic: BasicSubSettings
}

/** U盘卫士设置 */
export interface UsbGuardSettings {
  /** 开启U盘实时保护：1=开启，0=关闭 */
  protect_realtime: number
  /** U盘保护模式：0=大众模式，1=免打扰模式，2=打印店模式 */
  protect_mode: number
  /** 插入U盘后桌面任务栏生成"安全弹出U盘"小图标：1=开启，0=关闭 */
  create_trayicon: number
  /** 插入U盘后桌面右上角生成U盘快捷悬浮窗：1=开启，0=关闭 */
  show_suspendedwindow: number
  /** 禁用系统自带的U盘自动播放功能：1=禁用，0=不禁用 */
  forbid_autorun: number
  /** 开启扫描U盘隐藏文件夹：1=开启，0=关闭 */
  scan_hidepath: number
  /** 开启自动扫描：1=开启，0=关闭 */
  scan_auto: number
}

/** security 子字段（上网保护+U盘卫士+引擎设置） */
export interface SecuritySubSettings {
  /** 下载保护通知模式：1=总是通知，0=黑文件才通知 */
  download_always_remind: number
  /** 下载黑文件处理方式：1=自动删除，0=手动处理 */
  download_auto_delete: number
  /** U盘卫士设置 */
  usb: UsbGuardSettings
  /** 开启KVM云启发引擎：1=开启，0=关闭 */
  engine_kvm_open: number
  /** KVM引擎高敏感模式：1=开启，0=关闭 */
  engine_kvm_highlevel: number
  /** 离线杀毒模式：1=开启，0=关闭 */
  engine_kvm_offline: number
  /** 启用KBoost智能检测引擎：1=启用，0=禁用 */
  engine_kboost_open: number
  /** 启用捕风引擎：1=启用，0=禁用 */
  engine_kwindhunter_open: number
  /** 捕风引擎等级：1=最高，2=严格，3=智能，4=普通 */
  engine_kwindhunter_level: number
}

/** 安全保护设置（type=22 完整结构） */
export interface SecurityProtectionStrategy {
  /** 病毒查杀设置 */
  virusscan: VirusDetectionSettings
  /** 系统保护设置 */
  defend: SystemProtectionSettings
  /** 安全保护设置（上网保护+U盘卫士+引擎设置） */
  security: SecuritySubSettings
}

enum SelectorsUpdateMethod {
  regularExamSelectors = 'updateRegularExamSelectors',
  leakScanSelectors = 'updateLeakScanSelectors',
  leakRepairSelectors = 'updateLeakRepairSelectors',
  baseLineSelectors = 'updateBaseLineSelectors'
}
@Injectable()
export class GroupPolicySettingsService {
  baseLineScanType: Observable<number>
  baselineScanItemKeys: Observable<string[]>
  baselineScanCustomValues: Observable<BaselineScanCustomItem[]>
  customBaselineScanItemKeys: string[]
  customBaselineScanCustomValues: BaselineScanCustomItem[]
  hasEditTab: Observable<boolean>
  saveTab: Observable<string>
  regularExamSelectors: Observable<TaskTimeSetting[]>
  leakScanSelectors: Observable<TaskTimeSetting[]>
  leakRepairSelectors: Observable<TaskTimeSetting[]>
  baseLineSelectors: Observable<TaskTimeSetting[]>
  private updateEditTabEvent = new BehaviorSubject<boolean>(false)
  private updateSaveTabEvent = new BehaviorSubject<string>('')
  private updateRegularExamSelectorsEvent = new BehaviorSubject<TaskTimeSetting[]>([{ target: 1, start_min: 0, end_min: 10 }])
  private updateLeakScanSelectorsEvent = new BehaviorSubject<TaskTimeSetting[]>([{ target: 1, start_min: 0, end_min: 10 }])
  private updateLeakRepairSelectorsEvent = new BehaviorSubject<TaskTimeSetting[]>([{ target: 1, start_min: 0, end_min: 10 }])
  private updateBaseLineSelectorsEvent = new BehaviorSubject<TaskTimeSetting[]>([{ target: 1, start_min: 0, end_min: 10 }])
  private updateBaselineScanItemKeysEvent = new Subject<string[]>()
  private updateBaselineScanTypeEvent = new BehaviorSubject<number>(0)
  private updateBaselineScanCustomValuesEvent = new Subject<BaselineScanCustomItem[]>()
  private tabSwitchSaving = false
  constructor (private http: Http, private groupPolicyDrawer: GroupPolicyDrawerService) {
    this.customBaselineScanItemKeys = []
    this.customBaselineScanCustomValues = []
    this.baselineScanItemKeys = this.updateBaselineScanItemKeysEvent.asObservable()
    this.baselineScanCustomValues = this.updateBaselineScanCustomValuesEvent.asObservable()
    this.hasEditTab = this.updateEditTabEvent.asObservable()
    this.saveTab = this.updateSaveTabEvent.asObservable()
    this.regularExamSelectors = this.updateRegularExamSelectorsEvent.asObservable()
    this.leakScanSelectors = this.updateLeakScanSelectorsEvent.asObservable()
    this.leakRepairSelectors = this.updateLeakRepairSelectorsEvent.asObservable()
    this.baseLineSelectors = this.updateBaseLineSelectorsEvent.asObservable()
    this.baseLineScanType = this.updateBaselineScanTypeEvent.asObservable()
  }

  updateBaselineScanType (type: number) {
    this.updateBaselineScanTypeEvent.next(type)
  }

  updateBaselineScanItemKeys (keys: string[]) {
    this.updateBaselineScanItemKeysEvent.next(keys)
  }

  updateBaselineScanCustomValues (items: BaselineScanCustomItem[]) {
    this.customBaselineScanCustomValues = items
    this.updateBaselineScanCustomValuesEvent.next(items)
  }

  updateRegularExamSelectors (name: string, val: TaskTimeSetting[]) {
    // this[name] = val
    this.updateRegularExamSelectorsEvent.next(val)
  }

  updateLeakScanSelectors (name: string, val: TaskTimeSetting[]) {
    // this[name] = val
    this.updateLeakScanSelectorsEvent.next(val)
  }

  updateLeakRepairSelectors (name: string, val: TaskTimeSetting[]) {
    // this[name] = val
    this.updateLeakRepairSelectorsEvent.next(val)
  }

  updateBaseLineSelectors (name: string, val: TaskTimeSetting[]) {
    // this[name] = val
    this.updateBaseLineSelectorsEvent.next(val)
  }

  updateSelectors (name: string, val: TaskTimeSetting[]) {
    if (!val) {
      this[SelectorsUpdateMethod[name]](name, [{ target: 1, start_min: 0, end_min: 10 }])
      return
    }
    this[SelectorsUpdateMethod[name]](name, val)
  }

  updateEditTab (flag: boolean) {
    this.updateEditTabEvent.next(flag)
  }

  updateSaveTab (val: string) {
    this.tabSwitchSaving = true
    this.updateSaveTabEvent.next(val)
  }

  consumeTabSwitchSaving () {
    if (this.tabSwitchSaving) {
      this.tabSwitchSaving = false
      return true
    }
    return false
  }

  getSettings (options: QueryParams) {
    options.type = String(Math.floor(+options.type)) as any
    options.id = hasExistingStrategyId(options.id) ? options.id : undefined
    return this.http.post<SettingsRes>('/strategy/get', options).then(response => {
      const strategy: RegularPhysicalExaminationSettings | TimingAntivirusSettings | FlowControlSettings | OnlineBlackAndWhiteListSettings | EarlyWarningAndMonitoringSettings | LeakFixSettings | USBManageSettings = JSON.parse(response.strategy || '{}')
      response.strategy = strategy as any
      response.id = options.id || this.groupPolicyDrawer.policyData.id
      this.groupPolicyDrawer.changePolicyData(response)
      return response
    })
  }

  saveSettings (options: SaveParams) {
    if (!this.groupPolicyDrawer.policyData.name) {
      ElMessage.error('策略名称不能为空')
      return Promise.reject(false)
    }

    const url = hasExistingStrategyId(options.id) ? '/strategy/modify' : '/strategy/save'
    options.id = hasExistingStrategyId(options.id) ? options.id : undefined
    options.type = String(Math.floor(+options.type)) as any
    const bind_mode = options.bind_mode ?? 0
    return this.http.post(url, {
      ...options,
      bind_mode,
      group_ids: bind_mode === 0 ? (options.group_ids ?? []) : [],
      device_ids: bind_mode === 1 ? (options.device_ids ?? []) : [],
      name: this.groupPolicyDrawer.policyData.name,
    }).then(response => {
      ElMessage.success(i18n.global.t('mailSetting.saveSuccess'))
      if (!hasExistingStrategyId(options.id)) {
        this.groupPolicyDrawer.changePolicyData({
          ...this.groupPolicyDrawer.policyData,
          id: response.id
        })
      }
      return response
    })
  }

  getLeakRepairSystems () {
    return this.http.post<{
      list: OsOption[]
    }>('/group-strategy/leak-repair-version/get', {})
  }
}

function judgeRepairOSListLegal (osList: string[], options: OsOption[]) {
  let legalFlag = true
  const nameList = options.map((op: OsOption) => op.value)
  if (osList.length) {
    osList.forEach(os => {
      if (!nameList.includes(os)) {
        legalFlag = false
      }
    })
  }
  return legalFlag
}
export function setDefaultRepairData (strategy: any, options: OsOption[]) {
  if (!strategy.repair_os_list || !judgeRepairOSListLegal(strategy.repair_os_list, options)) {
    strategy.repair_os_list = []
  }
  if (!strategy.warn_level_list) {
    strategy.warn_level_list = []
  }
  return strategy
}
